export interface BeerStyleRow {
  id?: string;
  name?: string;
  type?: string;
}

export interface PortfolioBeerWithStyle {
  id?: string;
  style?: string;
}

export interface StyleMetaMap {
  [styleId: string]: { type: string; name: string };
}

const ALL_FILTER_VALUE = '';

/**
 * Builds a map of styleId → { type, name } from beerStyles collection rows.
 */
export function buildStyleMetaMap(beerStyles: BeerStyleRow[]): StyleMetaMap {
  const metaMap: StyleMetaMap = {};
  if (!Array.isArray(beerStyles)) {
    return metaMap;
  }

  beerStyles.forEach((styleRow) => {
    if (styleRow == null || typeof styleRow !== 'object') {
      return;
    }
    const styleId = typeof styleRow.id === 'string' ? styleRow.id : '';
    if (styleId.length === 0) {
      return;
    }
    const styleName =
      typeof styleRow.name === 'string' ? styleRow.name.trim() : '';
    const styleType =
      typeof styleRow.type === 'string' ? styleRow.type.trim() : '';
    metaMap[styleId] = {
      type: styleType.length > 0 ? styleType : 'Other',
      name: styleName.length > 0 ? styleName : styleId,
    };
  });

  return metaMap;
}

/**
 * Distinct style categories (beerStyles.type) present in the given beers.
 */
export function getDistinctStyleCategories(
  beers: PortfolioBeerWithStyle[],
  styleMetaMap: StyleMetaMap,
): string[] {
  const categories = new Set<string>();
  if (!Array.isArray(beers)) {
    return [];
  }

  beers.forEach((beer) => {
    const styleId = typeof beer.style === 'string' ? beer.style : '';
    if (styleId.length === 0) {
      return;
    }
    const meta = styleMetaMap[styleId];
    if (meta?.type) {
      categories.add(meta.type);
    }
  });

  return Array.from(categories).sort((left, right) =>
    left.localeCompare(right, undefined, { sensitivity: 'base' }),
  );
}

/**
 * Distinct style names (beerStyles.name) present in the given beers.
 */
export function getDistinctStyleNames(
  beers: PortfolioBeerWithStyle[],
  styleMetaMap: StyleMetaMap,
): string[] {
  const names = new Set<string>();
  if (!Array.isArray(beers)) {
    return [];
  }

  beers.forEach((beer) => {
    const styleId = typeof beer.style === 'string' ? beer.style : '';
    if (styleId.length === 0) {
      return;
    }
    const meta = styleMetaMap[styleId];
    if (meta?.name) {
      names.add(meta.name);
    }
  });

  return Array.from(names).sort((left, right) =>
    left.localeCompare(right, undefined, { sensitivity: 'base' }),
  );
}

/**
 * Filters beers by Style Category (beerStyles.type).
 * Empty categoryValue means no filter (all beers).
 */
export function filterBeersByStyleCategory(
  beers: PortfolioBeerWithStyle[],
  styleMetaMap: StyleMetaMap,
  categoryValue: string,
): PortfolioBeerWithStyle[] {
  if (!Array.isArray(beers)) {
    return [];
  }
  const normalized =
    typeof categoryValue === 'string' ? categoryValue.trim() : '';
  if (normalized.length === 0 || normalized === ALL_FILTER_VALUE) {
    return beers;
  }

  return beers.filter((beer) => {
    const styleId = typeof beer.style === 'string' ? beer.style : '';
    if (styleId.length === 0) {
      return false;
    }
    const meta = styleMetaMap[styleId];
    return meta?.type === normalized;
  });
}

/**
 * Filters beers by Style Name (beerStyles.name).
 * Empty nameValue means no filter (all beers).
 */
export function filterBeersByStyleName(
  beers: PortfolioBeerWithStyle[],
  styleMetaMap: StyleMetaMap,
  nameValue: string,
): PortfolioBeerWithStyle[] {
  if (!Array.isArray(beers)) {
    return [];
  }
  const normalized = typeof nameValue === 'string' ? nameValue.trim() : '';
  if (normalized.length === 0 || normalized === ALL_FILTER_VALUE) {
    return beers;
  }

  return beers.filter((beer) => {
    const styleId = typeof beer.style === 'string' ? beer.style : '';
    if (styleId.length === 0) {
      return false;
    }
    const meta = styleMetaMap[styleId];
    return meta?.name === normalized;
  });
}

/**
 * Applies both style category and style name filters in sequence.
 */
export function applyStyleFilters(
  beers: PortfolioBeerWithStyle[],
  styleMetaMap: StyleMetaMap,
  categoryValue: string,
  nameValue: string,
): PortfolioBeerWithStyle[] {
  const afterCategory = filterBeersByStyleCategory(
    beers,
    styleMetaMap,
    categoryValue,
  );
  return filterBeersByStyleName(afterCategory, styleMetaMap, nameValue);
}

export const PORTFOLIO_STYLE_FILTER_ALL = ALL_FILTER_VALUE;
